import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error('Invalid reset link');
      navigate('/login');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. Link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_gbaian-hub/artifacts/hv9nezmf_GBA%20LOGO%20PNG.png"
              alt="GBA Logo"
              className="h-20 mx-auto mb-4"
            />
            <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight">
              Reset Password
            </h1>
            <p className="text-slate-600 mt-2">Enter your new password</p>
          </div>

          {!success ? (
            <form onSubmit={handleSubmit} data-testid="reset-password-form">
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="new-password" className="text-sm font-semibold text-slate-700">
                    New Password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1 focus:ring-2 focus:ring-green-500"
                    data-testid="new-password-input"
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Must be at least 6 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="text-sm font-semibold text-slate-700">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1 focus:ring-2 focus:ring-green-500"
                    data-testid="confirm-password-input"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold py-3 shadow-lg shadow-green-500/20"
                data-testid="reset-password-button"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">
                Password Reset Successful!
              </h3>
              <p className="text-slate-600 mb-4">
                Your password has been reset successfully.
              </p>
              <p className="text-sm text-slate-500">
                Redirecting to login page...
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-white/70">
            ENHANCE • ELEVATE • EXCEL
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
